import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface AgingBucket {
  key: string
  label: string
  minHours: number
  maxHours: number | null
}

interface PendingCheckoutRow {
  sessionId: number
  sellerId: number
  sellerLabel: string
  startedAt: string
  finishedAt: string | null
  ageHours: number
  ageLabel: string
  bucketLabel: string
}

interface PendingCheckoutSellerSummary {
  sellerId: number
  sellerLabel: string
  pendingCount: number
  avgAgeHours: number
  maxAgeHours: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const hourFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const agingBuckets: AgingBucket[] = [
  { key: 'up-to-2h', label: 'Ate 2h', minHours: 0, maxHours: 2 },
  { key: '2h-to-8h', label: '2h a 8h', minHours: 2, maxHours: 8 },
  { key: '8h-to-24h', label: '8h a 24h', minHours: 8, maxHours: 24 },
  { key: 'over-24h', label: 'Mais de 24h', minHours: 24, maxHours: null },
]

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const parseDate = (value?: string | null): Date | null => {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const formatAgeLabel = (hours: number): string => {
  const totalMinutes = Math.max(0, Math.round(hours * 60))
  const dayCount = Math.floor(totalMinutes / 1440)
  const hourCount = Math.floor((totalMinutes % 1440) / 60)
  const minuteCount = totalMinutes % 60

  if (dayCount > 0) {
    return `${dayCount}d ${hourCount}h`
  }

  if (hourCount > 0) {
    return `${hourCount}h ${String(minuteCount).padStart(2, '0')}m`
  }

  return `${minuteCount}m`
}

const getBucketLabel = (ageHours: number): string => {
  const bucket = agingBuckets.find(({ minHours, maxHours }) => {
    const aboveMin = ageHours >= minHours
    const belowMax = maxHours === null || ageHours < maxHours
    return aboveMin && belowMax
  })

  return bucket?.label || 'Nao classificado'
}

const exportToCSV = (filename: string, bucketData: any[], sellerData: any[]): void => {
  let csv = 'sep=;\n'
  csv += 'RESUMO POR FAIXA ETÁRIA\n'
  csv += 'Faixa;Quantidade;Percentual (%)\n'

  bucketData.forEach((bucket) => {
    csv += `"${bucket.label}";${bucket.count};${bucket.share.toFixed(1)}\n`
  })

  csv += '\nRESUMO POR VENDEDOR\n'
  csv += 'Vendedor;Quantidade Pending;Aging Médio (h);Aging Máximo (h)\n'

  sellerData.forEach((row) => {
    csv += `"${row.sellerLabel}";${row.pendingCount};${row.avgAgeHours.toFixed(1)};${row.maxAgeHours.toFixed(1)}\n`
  })

  const bom = '\uFEFF'
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, bucketData: any[], sellerData: any[]): void => {
  const wb = XLSX.utils.book_new()

  const bucketRows = bucketData.map((bucket) => ({
    'Faixa': bucket.label,
    'Quantidade': bucket.count,
    'Percentual (%)': bucket.share.toFixed(1),
  }))

  const sellerRows = sellerData.map((row) => ({
    'Vendedor': row.sellerLabel,
    'Pending': row.pendingCount,
    'Aging Médio (h)': row.avgAgeHours.toFixed(1),
    'Aging Máximo (h)': row.maxAgeHours.toFixed(1),
  }))

  const wsBucket = XLSX.utils.json_to_sheet(bucketRows)
  const wsSeller = XLSX.utils.json_to_sheet(sellerRows)

  XLSX.utils.book_append_sheet(wb, wsBucket, 'Por Faixa')
  XLSX.utils.book_append_sheet(wb, wsSeller, 'Por Vendedor')
  XLSX.writeFile(wb, filename)
}

const exportToPDF = (
  filename: string,
  bucketData: any[],
  sellerData: any[],
  summary: any
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  // Header
  pdf.setFontSize(16)
  pdf.text('Relatório de Aging - Pending Checkout', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  const reportDate = new Date().toLocaleString('pt-BR')
  pdf.text(`Relatório gerado em: ${reportDate}`, margin, yPosition)
  yPosition += 12

  // Summary Section
  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  const summaryText = [
    `Total em Pending Checkout: ${summary.totalPending} | Críticos (24h+): ${summary.criticalCount}`,
    `Aging Médio: ${summary.avgAgeHours.toFixed(1)}h | Aging Máximo: ${summary.maxAgeHours.toFixed(1)}h`,
  ]

  summaryText.forEach((line) => {
    pdf.text(line, margin, yPosition)
    yPosition += 5
  })

  yPosition += 5

  // Bucket Table
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Distribuição por Faixa Etária', margin, yPosition)
  yPosition += 4

  const bucketTableData = bucketData.map((bucket) => [bucket.label, bucket.count, bucket.share.toFixed(1)])

  autoTable(pdf, {
    startY: yPosition,
    head: [['Faixa', 'Quantidade', 'Percentual (%)']],
    body: bucketTableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: 50,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  })

  yPosition = (pdf as any).lastAutoTable.finalY + 10

  // Seller Table
  pdf.setFontSize(10)
  pdf.text('Resumo por Vendedor', margin, yPosition)
  yPosition += 4

  const sellerTableData = sellerData.map((row) => [
    row.sellerLabel,
    row.pendingCount,
    row.avgAgeHours.toFixed(1),
    row.maxAgeHours.toFixed(1),
  ])

  autoTable(pdf, {
    startY: yPosition,
    head: [['Vendedor', 'Pending', 'Aging Médio (h)', 'Aging Máximo (h)']],
    body: sellerTableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: 50,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  })

  // Footer
  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  pdf.save(filename)
}

const SellerPendingCheckoutAgingReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const [filters, setFilters] = useState({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const pendingCheckoutRows = useMemo<PendingCheckoutRow[]>(() => {
    const now = Date.now()

    return sessions
      .filter((session) => session.outcome === 'pending_checkout')
      .map((session) => {
        const referenceDate = parseDate(session.finishedAt) || parseDate(session.startedAt)
        const ageHours = referenceDate ? Math.max(0, (now - referenceDate.getTime()) / 3600000) : 0

        return {
          sessionId: session.id,
          sellerId: session.sellerId,
          sellerLabel: getSellerLabel(session),
          startedAt: session.startedAt,
          finishedAt: session.finishedAt,
          ageHours,
          ageLabel: formatAgeLabel(ageHours),
          bucketLabel: getBucketLabel(ageHours),
        }
      })
      .sort((left, right) => right.ageHours - left.ageHours)
  }, [sessions])

  const summary = useMemo(() => {
    const totalPending = pendingCheckoutRows.length
    const avgAgeHours = totalPending
      ? pendingCheckoutRows.reduce((acc, row) => acc + row.ageHours, 0) / totalPending
      : 0
    const maxAgeHours = totalPending ? pendingCheckoutRows[0].ageHours : 0
    const criticalCount = pendingCheckoutRows.filter((row) => row.ageHours >= 24).length

    return {
      totalPending,
      avgAgeHours,
      maxAgeHours,
      criticalCount,
    }
  }, [pendingCheckoutRows])

  const bucketSummary = useMemo(() => {
    return agingBuckets.map((bucket) => {
      const count = pendingCheckoutRows.filter((row) => row.bucketLabel === bucket.label).length
      const share = summary.totalPending ? (count / summary.totalPending) * 100 : 0

      return {
        ...bucket,
        count,
        share,
      }
    })
  }, [pendingCheckoutRows, summary.totalPending])

  const sellerSummary = useMemo<PendingCheckoutSellerSummary[]>(() => {
    const grouped = new Map<number, PendingCheckoutSellerSummary>()

    pendingCheckoutRows.forEach((row) => {
      const current = grouped.get(row.sellerId)
      if (current) {
        current.pendingCount += 1
        current.avgAgeHours += row.ageHours
        current.maxAgeHours = Math.max(current.maxAgeHours, row.ageHours)
        return
      }

      grouped.set(row.sellerId, {
        sellerId: row.sellerId,
        sellerLabel: row.sellerLabel,
        pendingCount: 1,
        avgAgeHours: row.ageHours,
        maxAgeHours: row.ageHours,
      })
    })

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        avgAgeHours: row.pendingCount ? row.avgAgeHours / row.pendingCount : 0,
      }))
      .sort((left, right) => right.pendingCount - left.pendingCount || right.maxAgeHours - left.maxAgeHours)
  }, [pendingCheckoutRows])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: 'Aging de Pending Checkout', path: '/seller/reports/pending-checkout-aging', isActive: true },
        ]}
      >
        Relatório de Aging de Pending Checkout
      </PageTitle>

      <ReportShell
        title='Aging de Pending Checkout'
        description='Mostra há quanto tempo atendimentos em pending checkout permanecem sem conversão, com visão por faixa e por vendedor.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToCSV(`Aging-Pending-${timestamp}.csv`, bucketSummary, sellerSummary)
              }}
              disabled={loading || bucketSummary.length === 0}
              title='Baixar relatório em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToXLSX(`Aging-Pending-${timestamp}.xlsx`, bucketSummary, sellerSummary)
              }}
              disabled={loading || bucketSummary.length === 0}
              title='Baixar relatório em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToPDF(`Aging-Pending-${timestamp}.pdf`, bucketSummary, sellerSummary, summary)
              }}
              disabled={loading || bucketSummary.length === 0}
              title='Baixar relatório em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>Pluginspace nao identificado para o usuario logado.</div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Pendentes de checkout</div>
                    <div className='fs-2hx fw-bold text-warning'>
                      {numberFormatter.format(summary.totalPending)}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Idade media</div>
                    <div className='fs-2 fw-bold text-dark'>{hourFormatter.format(summary.avgAgeHours)}h</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Maior aging</div>
                    <div className='fs-2 fw-bold text-danger'>{formatAgeLabel(summary.maxAgeHours)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Criticos (+24h)</div>
                    <div className='fs-2hx fw-bold text-danger'>
                      {numberFormatter.format(summary.criticalCount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm mb-4'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Distribuicao por faixa de aging</span>
                  <span className='text-gray-600 mt-1'>Faixas operacionais para priorizar follow-up de conversão.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                <div className='row g-4'>
                  {bucketSummary.map((bucket) => (
                    <div className='col-12 col-md-6 col-xl-3' key={bucket.key}>
                      <div className='card border border-dashed h-100'>
                        <div className='card-body'>
                          <div className='text-gray-700 fw-semibold mb-2'>{bucket.label}</div>
                          <div className='fs-2 fw-bold text-dark'>{numberFormatter.format(bucket.count)}</div>
                          <div className='text-gray-600 mt-1'>{hourFormatter.format(bucket.share)}% da fila pendente</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm mb-4'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Resumo por vendedor</span>
                  <span className='text-gray-600 mt-1'>Quantidade pendente e tempo médio/máximo por vendedor.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerSummary.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posição</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Pendentes</th>
                          <th className='text-end'>Idade média</th>
                          <th className='text-end'>Maior aging</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerSummary.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.pendingCount)}</td>
                            <td className='text-end'>{hourFormatter.format(row.avgAgeHours)}h</td>
                            <td className='text-end fw-semibold text-danger'>{formatAgeLabel(row.maxAgeHours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Nenhum pending checkout encontrado para os filtros selecionados.</div>
                )}
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Lista detalhada de pending checkout</span>
                  <span className='text-gray-600 mt-1'>Ordenado do maior aging para o menor.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : pendingCheckoutRows.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Session</th>
                          <th>Vendedor</th>
                          <th>Inicio</th>
                          <th>Fim do atendimento</th>
                          <th className='text-end'>Aging</th>
                          <th className='text-end'>Faixa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingCheckoutRows.map((row) => (
                          <tr key={row.sessionId}>
                            <td>#{row.sessionId}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td>{new Date(row.startedAt).toLocaleString('pt-BR')}</td>
                            <td>{row.finishedAt ? new Date(row.finishedAt).toLocaleString('pt-BR') : '-'}</td>
                            <td className='text-end fw-semibold text-danger'>{row.ageLabel}</td>
                            <td className='text-end'>{row.bucketLabel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Nenhum pending checkout encontrado para os filtros selecionados.</div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerPendingCheckoutAgingReportPage }
